/**
 * What a consignment is, in the vocabulary the database uses.
 *
 * A work handed to a gallery to sell. It is NOT a sale and NOT a transfer of
 * ownership: it authorises a gallery to offer the work, opens the record to
 * them, and lets them edit the catalogue. Title does not move — a consignment is
 * the AUTHORITY a sale is later made under, and the sale itself is a separate
 * `ondo.transfers` row written by `ondo.effect_sale()`, which ends this
 * arrangement with `outcome = 'sold'` in the same statement.
 *
 * Must stay in step with the `ondo.consignment_*` enums in
 * supabase/migrations/20260902090000_consignments.sql. Nothing enforces that
 * but this comment, which is the arrangement every vocabulary here has.
 */
/**
 * How the work came to be with the gallery.
 *
 * `primary_representation` is the standing arrangement a gallery has with an
 * artist it represents, rather than a document signed about one object — which
 * is why the inventory row prints it *instead of* a consigned date.
 */
export const CONSIGNMENT_ARRANGEMENTS = ["consignment", "primary_representation"];
/**
 * What the parties agreed about price.
 *
 * `unspecified` is a real answer and not a missing one — plenty of works hang
 * with an asking price and no floor agreed — which is why it is a value rather
 * than a null.
 */
export const CONSIGNMENT_BASES = ["net_of_sale", "min_price_agreed", "unspecified"];
/**
 * How it ended. `declined` answers an offer; the other four end a live
 * arrangement. There is no `deleted`: a consignment is ended, never removed.
 */
export const CONSIGNMENT_OUTCOMES = [
    "sold",
    "returned",
    "withdrawn",
    "declined",
    "expired",
];
/**
 * How a proposed REVISION of the terms finished.
 *
 * `accepted` is the only one that moves a price: it means every party to the
 * agreement put their name to that exact version, which is the whole of OND-101.
 * `rejected` is one party refusing, which settles it because the rule is that
 * all of them accept. `lapsed` is the consignment itself ending underneath a
 * proposal nobody had answered.
 *
 * There is no `withdrawn`, and the absence is deliberate: a proposer who
 * mistyped is answered with a refusal by the other side, which is one press on
 * the screen they are already looking at. A withdrawal door is a fourth value
 * and an argument about whether unsaying a proposal should tell the party who
 * was reading it.
 *
 * Must stay in step with `ondo.consignment_revision_outcome` in
 * supabase/migrations/20261003095000_consignment_revisions.sql.
 */
export const CONSIGNMENT_REVISION_OUTCOMES = ["accepted", "rejected", "lapsed"];
/**
 * One party's answer to one exact version of the terms.
 *
 * Two values, because "every party must accept the same exact version" has
 * exactly one alternative. A party answers once: there is no `withdraw` here
 * either, and `ondo.consignment_revision_responses`'s primary key is what makes
 * a repeated acceptance the same row rather than a second one.
 */
export const CONSIGNMENT_REVISION_STANCES = ["accept", "reject"];
export function isConsignmentBasis(value) {
    return CONSIGNMENT_BASES.includes(value);
}
export function isConsignmentArrangement(value) {
    return CONSIGNMENT_ARRANGEMENTS.includes(value);
}
/**
 * Whether a live consignment has been answered.
 *
 * A predicate over the row rather than a stored status, for the reason the whole
 * schema prefers one: `accepted_at` is the fact, and a `status` column beside it
 * would be a second place for the same truth to live and disagree.
 */
export function isAccepted(consignment) {
    return consignment.acceptedAt !== null;
}
/**
 * Whether a proposed revision is still awaiting an answer.
 *
 * `endedAt` AND NOT `outcome`, although the two arrive together —
 * `consignment_revisions_ending_is_complete` guarantees it — because the same
 * predicate then reads the same way as `isAccepted` above and as every other
 * "is this live" question in this domain. One spelling for one shape of fact.
 */
export function isRevisionPending(revision) {
    return revision.endedAt === null;
}
/**
 * Whether these terms are the ones a sale would be held to.
 *
 * `effectiveAt` is set in the same statement as the last required acceptance, so
 * this is the only test a reader needs: a pending proposal and a refused one
 * both answer false, which is OND-101's "A pending or rejected proposal must not
 * change the effective asking price or floor" as a function rather than a rule
 * somebody has to remember.
 */
export function isRevisionEffective(revision) {
    return revision.effectiveAt !== null;
}
/**
 * Formats minor units for display. `1_450_000_00` GBP → '£1,450,000'.
 *
 * Whole units only, which is the registry's house style for money the way
 * `fmt()` is in the design reference: a price of £1,450,000.00 reads as a
 * spreadsheet, and the pence are never the point at these amounts. The minor
 * units are still what is stored and summed — this rounds only the rendering.
 */
export function formatMoney(money, locale = "en-GB") {
    if (!money)
        return "";
    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: money.currency,
            maximumFractionDigits: 0,
        }).format(money.minor / 100);
    }
    catch {
        // An unknown or malformed currency code must not blank a whole row. The
        // number is the half the reader needs; the code is the half they can infer.
        return `${money.currency} ${Math.round(money.minor / 100).toLocaleString(locale)}`;
    }
}
//# sourceMappingURL=consignment.js.map